import React, {useEffect, useMemo, useState} from 'react';
import PropTypes from 'prop-types';
import Icon from '../components/icons/Icon';
import { useAuth } from '../context/AuthContext';
import { getUsageSummary } from '../services/UsageService';
import './Activity.css';

const WEEKS = 53;
const DAY_MS = 86400000;

function formatTokens(n) {
  if (n >= 1_000_000) return `${(n / 1_000_000).toFixed(1)}M`;
  if (n >= 1_000) return `${(n / 1_000).toFixed(1)}k`;
  return String(n);
}

function toDateKey(date) {
  return date.toISOString().slice(0, 10);
}

/**
 * Builds a 53-week x 7-day grid ending today (GitHub-contribution-graph
 * shape), left-padded so the first column is a real Sunday-start week —
 * cells before the data window (day 0 of the app, or before the 371-day
 * fetch window) render as "no data" rather than zero activity, so a new
 * user's mostly-empty graph doesn't read as 370 days of a broken streak.
 */
function buildWeeks(dailyActivity) {
  const byDay = new Map(dailyActivity.map((d) => [d.day, d]));
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const totalDays = WEEKS * 7;
  const start = new Date(today.getTime() - (totalDays - 1) * DAY_MS);
  // Roll back to the preceding Sunday so week columns line up.
  start.setDate(start.getDate() - start.getDay());

  const days = [];
  const cursor = new Date(start);
  while (cursor <= today) {
    const key = toDateKey(cursor);
    days.push({
      key,
      date: new Date(cursor),
      inWindow: cursor.getTime() >= today.getTime() - 370 * DAY_MS,
      entry: byDay.get(key) || null,
    });
    cursor.setDate(cursor.getDate() + 1);
  }

  const weeks = [];
  for (let i = 0; i < days.length; i += 7) {
    weeks.push(days.slice(i, i + 7));
  }
  return weeks;
}

/** Sequential, single-hue intensity scale (opacity steps of the app's own
 * accent token) — a magnitude encoding needs one hue light->dark, never a
 * rainbow (see the dataviz skill's color-formula rule). Reusing the app's
 * existing --accent-primary keeps this on-brand without inventing a second
 * palette system. */
function intensityClass(messages) {
  if (messages === 0) return 'level-0';
  if (messages < 3) return 'level-1';
  if (messages < 8) return 'level-2';
  if (messages < 20) return 'level-3';
  return 'level-4';
}

function StatTile({icon, label, value, hint}) {
  return (
    <div className="activity-stat-tile">
      <div className="activity-stat-icon"><Icon name={icon} size={18} /></div>
      <div className="activity-stat-body">
        <div className="activity-stat-value">{value}</div>
        <div className="activity-stat-label">{label}</div>
        {hint && <div className="activity-stat-hint">{hint}</div>}
      </div>
    </div>
  );
}

StatTile.propTypes = {
  hint: PropTypes.string,
  icon: PropTypes.string.isRequired,
  label: PropTypes.string.isRequired,
  value: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
};

StatTile.defaultProps = {
  hint: null,
};

function Activity({embedded}) {
  const { user } = useAuth();
  const userId = user?.sub || user?.id || user?.email;

  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [hoveredDay, setHoveredDay] = useState(null);

  useEffect(() => {
    if (!userId) return undefined;
    let cancelled = false;
    setLoading(true);
    getUsageSummary(userId).then((data) => {
      if (!cancelled) setSummary(data);
    }).catch((err) => {
      if (!cancelled) setError(err?.message || 'Failed to load activity');
    }).finally(() => {
      if (!cancelled) setLoading(false);
    });
    return () => { cancelled = true; };
  }, [userId]);

  const weeks = useMemo(() => buildWeeks(summary?.dailyActivity || []), [summary]);
  const monthLabels = useMemo(() => {
    const labels = [];
    let lastMonth = null;
    weeks.forEach((week, i) => {
      const firstRealDay = week.find((d) => d.date.getDate() <= 7);
      if (firstRealDay) {
        const month = firstRealDay.date.getMonth();
        if (month !== lastMonth) {
          labels.push({ index: i, label: firstRealDay.date.toLocaleDateString(undefined, {month: 'short'}) });
          lastMonth = month;
        }
      }
    });
    return labels;
  }, [weeks]);

  return (
    <div className={embedded ? 'activity-page activity-page-embedded' : 'activity-page'}>
      {!embedded && (
        <div className="activity-header">
          <div className="activity-title-row">
            <Icon name="bar-chart" size={22} />
            <h1 className="activity-title">Activity</h1>
          </div>
          <p className="activity-subtitle">Your usage across every project — sessions, messages, tokens, and daily streaks.</p>
        </div>
      )}

      {loading ? (
        <div className="activity-loading">Loading activity…</div>
      ) : error ? (
        <div className="activity-error">{error}</div>
      ) : (
        <>
          <div className="activity-stats-row">
            <StatTile icon="message" label="Sessions" value={summary.totalSessions.toLocaleString()} />
            <StatTile icon="clock" label="Messages" value={summary.totalMessages.toLocaleString()} />
            <StatTile
              hint={`${formatTokens(summary.totalInputTokens)} in / ${formatTokens(summary.totalOutputTokens)} out`}
              icon="database"
              label="Tokens"
              value={formatTokens(summary.totalTokens)}
            />
            <StatTile
              hint={`Longest: ${summary.longestStreakDays}d`}
              icon="zap"
              label="Current streak"
              value={`${summary.currentStreakDays}d`}
            />
          </div>

          <div className="activity-heatmap-card">
            <div className="activity-heatmap-header">
              <span>Daily activity</span>
              <div className="activity-heatmap-legend">
                <span>Less</span>
                {['level-0', 'level-1', 'level-2', 'level-3', 'level-4'].map((cls) => (
                  <span className={`activity-legend-swatch ${cls}`} key={cls} />
                ))}
                <span>More</span>
              </div>
            </div>
            <div className="activity-heatmap-scroll">
              <div className="activity-heatmap-months">
                {monthLabels.map((m) => (
                  <span
                    className="activity-heatmap-month-label"
                    key={`${m.index}-${m.label}`}
                    style={{ gridColumnStart: m.index + 1 }}
                  >
                    {m.label}
                  </span>
                ))}
              </div>
              <div className="activity-heatmap-grid">
                {weeks.map((week, wi) => (
                  <div className="activity-heatmap-week" key={wi}>
                    {week.map((day) => {
                      const messages = day.entry?.messages || 0;
                      const cls = day.inWindow ? intensityClass(messages) : 'level-none';
                      return (
                        <div
                          className={`activity-heatmap-cell ${cls}`}
                          key={day.key}
                          onMouseEnter={() => setHoveredDay(day)}
                          onMouseLeave={() => setHoveredDay((prev) => (prev?.key === day.key ? null : prev))}
                          title={day.inWindow
                            ? `${day.date.toLocaleDateString()}: ${messages} message${messages === 1 ? '' : 's'}, ${formatTokens(day.entry?.tokens || 0)} tokens`
                            : undefined}
                        />
                      );
                    })}
                  </div>
                ))}
              </div>
            </div>
            {hoveredDay?.inWindow && (
              <div className="activity-heatmap-tooltip">
                {hoveredDay.date.toLocaleDateString(undefined, {weekday: 'long', month: 'short', day: 'numeric'})}
                {' — '}
                {(hoveredDay.entry?.messages || 0)} message{(hoveredDay.entry?.messages || 0) === 1 ? '' : 's'},
                {' '}{formatTokens(hoveredDay.entry?.tokens || 0)} tokens
              </div>
            )}
          </div>
        </>
      )}
    </div>
  );
}

Activity.propTypes = {
  embedded: PropTypes.bool,
};

Activity.defaultProps = {
  embedded: false,
};

export default Activity;
